import { useState, useEffect, useRef } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { findMyDriverProfile } from '@/lib/myDriverProfile';
import { Send, MessageSquare, Shield, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function LoadChat({ load }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  // user.full_name comes from Supabase Auth metadata, which is frequently
  // never set for driver accounts -- their real name lives on their
  // DriverProfile (filled in at onboarding) instead. Without this, a chat
  // message from a driver whose auth metadata has no name shows their
  // email as the sender to dispatch.
  const [driverFullName, setDriverFullName] = useState(null);

  useEffect(() => {
    if (user?.email) findMyDriverProfile(user.email).then(p => setDriverFullName(p?.full_name || null));
  }, [user?.email]);

  useEffect(() => {
    if (!load?.id) return;
    fetchMessages();

    const unsubscribe = client.entities.LoadMessage.subscribe((event) => {
      if (event.data?.load_id !== load.id) return;
      if (event.type === 'create') {
        setMessages(prev => prev.some(m => m.id === event.data.id) ? prev : [...prev, event.data]);
      }
    });

    return () => unsubscribe();
  }, [load?.id]);

  // Scrolls only the chat's own message list. scrollIntoView on the
  // bottom sentinel also scrolls every ancestor -- i.e. the whole Active
  // Load page -- so opening the page jumped straight down to the chat.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    const data = await client.entities.LoadMessage.filter({ load_id: load.id }, 'created_date', 100);
    setMessages(data);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setSending(true);
    setInput('');
    const saved = await client.entities.LoadMessage.create({
      load_id: load.id,
      load_number: load.load_number,
      sender_email: user.email,
      sender_name: driverFullName || user.full_name || user.email,
      sender_role: user.role || 'user',
      message: text,
    });
    // Optimistically add to local state; subscription may also fire so dedupe by id
    setMessages(prev => prev.some(m => m.id === saved.id) ? prev : [...prev, saved]);
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isMe = (msg) => msg.sender_email === user.email;
  const isAdmin = (msg) => msg.sender_role === 'admin';

  return (
    <div className="glass-card flex flex-col" style={{ height: '420px' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Load #{load.load_number} — Dispatch Chat</p>
          <p className="text-xs text-muted-foreground">Real-time communication with your dispatcher</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs text-muted-foreground">Live</span>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No messages yet.</p>
            <p className="text-xs text-muted-foreground mt-0.5">Send a message to get help from your dispatcher.</p>
          </div>
        )}
        {messages.map((msg) => {
          const mine = isMe(msg);
          const admin = isAdmin(msg);
          return (
            <div key={msg.id} className={cn("flex gap-2", mine ? "justify-end" : "justify-start")}>
              {!mine && (
                <div className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5",
                  admin ? "bg-primary/20" : "bg-white/10"
                )}>
                  {admin
                    ? <Shield className="w-3.5 h-3.5 text-primary" />
                    : <Truck className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
              )}
              <div className={cn("max-w-[75%]", mine ? "items-end" : "items-start", "flex flex-col gap-0.5")}>
                {!mine && (
                  <span className="text-xs text-muted-foreground px-1">
                    {msg.sender_name}{admin ? ' · Dispatcher' : ''}
                  </span>
                )}
                <div className={cn(
                  "px-3.5 py-2 rounded-2xl text-sm leading-relaxed",
                  mine
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-white/8 border border-border text-foreground rounded-bl-sm"
                )}>
                  {msg.message}
                </div>
                <span className="text-xs text-muted-foreground px-1">
                  {new Date(msg.created_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border flex gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Enter to send)"
          rows={1}
          className="flex-1 px-3 py-2 rounded-xl bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
        />
        <button
          onClick={handleSend}
          disabled={sending || !input.trim()}
          className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 hover:bg-primary/90 disabled:opacity-40 transition-colors self-end"
        >
          <Send className="w-4 h-4 text-primary-foreground" />
        </button>
      </div>
    </div>
  );
}